import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axios } from '../../../shared/clients';
import { retryRequest } from '../../../shared/utils';
import { STATUS } from '../../../shared/enums';
import { addGlobalError } from '../root/rootSlice';

export const initialState = {
  data: null,
  status: STATUS.IDLE,
  error: null,
};

// The regional-prices backend keys the UK bucket as 'UK', while the app's
// canonical region code for the UK is 'GB'. Translate only at this boundary.
const REGIONAL_PRICE_API_REGION = { GB: 'UK' };

export const fetchRegionalPrices = createAsyncThunk('regionalPrices/fetchRegionalPrices', async (payload, { rejectWithValue, dispatch }) => {
  try {
    const response = await retryRequest(() => axios.get('/regional-prices'));
    const apiRegion = REGIONAL_PRICE_API_REGION[payload.region] ?? payload.region;
    return response?.data[apiRegion]?.map(filterPrice => {
      return {
        ...filterPrice,
        product: filterPrice.variant,
      }
    })
  } catch (e) {
    dispatch(addGlobalError({ ...e.response?.data, status: e.response?.status }));
    return rejectWithValue({ ...e.response?.data, status: e.response?.status });
  }
});

/**
 * @type {Slice<{data: null, error: null, status: string}, {clearRegionalPrices: reducers.clearRegionalPrices}, string>}
 */
export const regionalPricesSlice = createSlice({
  name: 'regionalPrices',
  initialState,
  reducers: {
    clearRegionalPrices: state => {
      state.data = initialState.data;
      state.status = initialState.status;
      state.error = initialState.error;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRegionalPrices.pending, state => {
        state.status = STATUS.LOADING;
      })
      .addCase(fetchRegionalPrices.fulfilled, (state, action) => {
        state.status = IS_SERVER ? STATUS.PREFETCHED : STATUS.SUCCEEDED;
        state.data = action.payload;
        state.error = initialState.error;
      })
      .addCase(fetchRegionalPrices.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload;
      });
  },
});

export const { clearRegionalPrices } = regionalPricesSlice.actions;

export const selectRegionalPricesData = state => state.regionalPrices.data;
export const selectRegionalPricesStatus = state => state.regionalPrices.status;
export const selectRegionalPricesError = state => state.regionalPrices.error;

export default regionalPricesSlice.reducer;
